import React, { useEffect, useState } from 'react';
import { useToasts } from 'react-toast-notifications';
import { Row, Col, Button, Card, CardBody, Table, Badge, Input, FormGroup, Label, Collapse, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { withTranslation } from 'react-multi-lang';
import { faRefresh, faSync, faFilter, faChevronDown, faChevronUp, faPlus } from "@fortawesome/free-solid-svg-icons";
import FaultsService from '../Services/FaultsService';
import { DateTime } from 'luxon';
import AuthService from '../Services/AuthService';
import Select from 'react-select';

const Faults = ({ t }) => {
    const [faults, setFaults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [reasons, setReasons] = useState([]);
    const [machines, setMachines] = useState([]);
    const [areas, setAreas] = useState([]);
    const [expandedRows, setExpandedRows] = useState([]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const authService = new AuthService();
    const config = authService.getConfig();
    const [createForm, setCreateForm] = useState({
        machineId: '',
        wh: config ? config.whCode : '',
        shelf: '',
        reason: '',
        message: '',
        isOperable: false
    });
    const [filters, setFilters] = useState({
        machineId: '',
        wh: config ? config.whCode : '',
        shelf: '',
        reason: '',
        isSyncedWithERP: '',
        isResolved: 'false',
        isOperable: '',
        timeFrom: '',
        timeTo: ''
    });
    const [showFilters, setShowFilters] = useState(false);

    const faultsService = new FaultsService();
    const { addToast } = useToasts();

    useEffect(() => {
        const handleWarehouseChange = (e) => {
            const conf = (e && e.detail) ? e.detail : authService.getConfig();
            const wh = conf ? conf.whCode : '';
            setFilters(prev => ({
                ...prev,
                wh: wh
            }));
            setCreateForm(prev => ({
                ...prev,
                wh: wh
            }));
            if (conf && conf.shelves) {
                setAreas(conf.shelves.map(s => ({ value: s, label: s })));
            } else {
                setAreas([]);
            }
            getDistinctValues(conf);
            getFaults(wh);
        };

        getFaults();
        getReasons();
        getDistinctValues();
        if (config && config.shelves) {
            setAreas(config.shelves.map(s => ({ value: s, label: s })));
        }

        window.addEventListener('warehouseChanged', handleWarehouseChange);
        window.addEventListener('storage', handleWarehouseChange);
        return () => {
            window.removeEventListener('warehouseChanged', handleWarehouseChange);
            window.removeEventListener('storage', handleWarehouseChange);
        };
    }, []);

    const getFaults = (customWh) => {
        setLoading(true);
        const validCustomWh = typeof customWh === 'string' ? customWh : undefined;
        const currentWh = validCustomWh !== undefined ? validCustomWh : (filters.wh !== undefined && filters.wh !== '' ? filters.wh : (config ? config.whCode : ''));
        // Clean filters
        const cleanFilters = {};
        Object.keys(filters).forEach(key => {
            if (key === 'wh') {
                if (currentWh !== '') {
                    cleanFilters[key] = currentWh;
                }
            } else if (filters[key] !== '') {
                cleanFilters[key] = filters[key];
            }
        });
        if (validCustomWh !== undefined) {
            if (validCustomWh !== '') {
                cleanFilters.wh = validCustomWh;
            } else {
                delete cleanFilters.wh;
            }
        }

        faultsService.getFaults(cleanFilters)
            .then(r => {
                setFaults(r);
            })
            .catch(e => addToast(e.toString(), { appearance: 'error', autoDismiss: true }))
            .finally(() => setLoading(false));
    };

    const getReasons = () => {
        faultsService.api.get('faults/reasons').then(r => setReasons(r.data)).catch(console.error);
    };

    const getDistinctValues = (currentConfig = authService.getConfig()) => {
        const wh = currentConfig ? currentConfig.whCode : '';
        const company = currentConfig ? currentConfig.company : '';
        const params = new URLSearchParams();
        if (wh) params.append('wh', wh);
        if (company) params.append('company', company);
        const queryString = params.toString();
        faultsService.api.get(`faults/distinct-values${queryString ? `?${queryString}` : ''}`)
            .then(r => {
                setMachines(r.data.machines.map(m => ({ 
                    value: m.id, 
                    label: m.name ? `${m.id} - ${m.name}` : m.id 
                })));
                // We are now taking areas from config, but we could merge them with r.data.areas if needed
                // For now, let's stick to the requirement: "the areas should be taken from the ui, from the currently selected config"
            })
            .catch(console.error);
    };

    const handleFilterChange = (e) => {
        setFilters({
            ...filters,
            [e.target.name]: e.target.value
        });
    };

    const handleSelectFilterChange = (selectedOption, field) => {
        setFilters({
            ...filters,
            [field]: selectedOption ? selectedOption.value : ''
        });
    };

    const toggleSync = (id) => {
        faultsService.toggleSync(id)
            .then(() => {
                addToast(t('faults.syncSuccess'), { appearance: 'success', autoDismiss: true });
                getFaults();
            })
            .catch(e => addToast(e.toString(), { appearance: 'error', autoDismiss: true }));
    };

    const resolveFault = (id) => {
        if (window.confirm(t('faults.confirmResolve') || 'Are you sure you want to mark this fault as resolved?')) {
            faultsService.resolve(id)
                .then(() => {
                    addToast(t('faults.resolveSuccess') || 'Fault resolved successfully', { appearance: 'success', autoDismiss: true });
                    getFaults();
                })
                .catch(e => addToast(e.toString(), { appearance: 'error', autoDismiss: true }));
        }
    };

    const handleCreateFault = () => {
        if (!createForm.machineId || !createForm.reason) {
            addToast('Please select machine and reason', { appearance: 'warning', autoDismiss: true });
            return;
        }
        const user = authService.getUser();
        const currentConf = authService.getConfig();
        setCreateLoading(true);
        faultsService.createFault({
            userId: user ? (user.userId || user.id || 1) : 1,
            machineId: createForm.machineId,
            wh: createForm.wh || (currentConf ? currentConf.whCode : undefined),
            shelf: createForm.shelf || undefined,
            reason: createForm.reason,
            message: createForm.message || '',
            isOperable: createForm.isOperable
        }).then(() => {
            addToast(t('faults.createFaultSuccess') || 'Fault recorded successfully', { appearance: 'success', autoDismiss: true });
            setShowCreateModal(false);
            setCreateForm({
                machineId: '',
                wh: currentConf ? currentConf.whCode : '',
                shelf: '',
                reason: '',
                message: '',
                isOperable: false
            });
            getFaults();
        }).catch(e => {
            addToast(t('faults.createFaultError') || e.toString(), { appearance: 'error', autoDismiss: true });
        }).finally(() => {
            setCreateLoading(false);
        });
    };

    const toggleRow = (id) => {
        if (expandedRows.includes(id)) {
            setExpandedRows(expandedRows.filter(rowId => rowId !== id));
        } else {
            setExpandedRows([...expandedRows, id]);
        }
    };

    return (
        <Card style={{ padding: '20px' }}>
            <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h3>{t('faults.title')}</h3>
                    <div>
                        <Button color="danger" className="me-2" onClick={() => setShowCreateModal(true)}>
                            <FontAwesomeIcon icon={faPlus} /> {t('faults.reportFault') || 'Καταγραφή Βλάβης'}
                        </Button>
                        <Button color="info" className="me-2" onClick={() => setShowFilters(!showFilters)}>
                            <FontAwesomeIcon icon={faFilter} /> {showFilters ? t('faults.hideFilters') : t('faults.showFilters')}
                        </Button>
                        <Button color="secondary" onClick={() => getFaults()} disabled={loading}>
                            <FontAwesomeIcon icon={faRefresh} spin={loading} />
                        </Button>
                    </div>
                </div>

                {showFilters && (
                    <Card className="mb-4 bg-light">
                        <CardBody>
                            <Row>
                                <Col md="3">
                                    <FormGroup>
                                        <Label>{t('faults.machine')}</Label>
                                        <Select
                                            isClearable
                                            options={machines}
                                            value={machines.find(m => m.value === filters.machineId)}
                                            onChange={(opt) => handleSelectFilterChange(opt, 'machineId')}
                                            placeholder={t('faults.machinePlaceholder')}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <FormGroup>
                                        <Label>{t('faults.shelf')}</Label>
                                        <Select
                                            isClearable
                                            options={areas}
                                            value={areas.find(a => a.value === filters.shelf)}
                                            onChange={(opt) => handleSelectFilterChange(opt, 'shelf')}
                                            placeholder={t('faults.shelfPlaceholder')}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <FormGroup>
                                        <Label>{t('faults.reason')}</Label>
                                        <Input type="select" name="reason" value={filters.reason} onChange={handleFilterChange}>
                                            <option value="">{t('faults.allReasons')}</option>
                                            {reasons.map(r => <option key={r} value={r}>{r}</option>)}
                                        </Input>
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <FormGroup>
                                        <Label>{t('faults.operability') || 'Machine Status'}</Label>
                                        <Input type="select" name="isOperable" value={filters.isOperable} onChange={handleFilterChange}>
                                            <option value="">{t('faults.allOperability') || t('faults.all')}</option>
                                            <option value="true">{t('faults.operableShort') || 'Λειτουργικό'}</option>
                                            <option value="false">{t('faults.disabledShort') || 'Εκτός λειτουργίας'}</option>
                                        </Input>
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="3">
                                    <FormGroup>
                                        <Label>{t('faults.synced')}</Label>
                                        <Input type="select" name="isSyncedWithERP" value={filters.isSyncedWithERP} onChange={handleFilterChange}>
                                            <option value="">{t('faults.all')}</option>
                                            <option value="true">{t('faults.yes')}</option>
                                            <option value="false">{t('faults.no')}</option>
                                        </Input>
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <FormGroup>
                                        <Label>{t('faults.resolved') || 'Resolved'}</Label>
                                        <Input type="select" name="isResolved" value={filters.isResolved} onChange={handleFilterChange}>
                                            <option value="">{t('faults.all')}</option>
                                            <option value="true">{t('faults.yes')}</option>
                                            <option value="false">{t('faults.no')}</option>
                                        </Input>
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <FormGroup>
                                        <Label>{t('faults.timeFrom')}</Label>
                                        <Input type="date" name="timeFrom" value={filters.timeFrom} onChange={handleFilterChange} />
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <FormGroup>
                                        <Label>{t('faults.timeTo')}</Label>
                                        <Input type="date" name="timeTo" value={filters.timeTo} onChange={handleFilterChange} />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <div className="text-end">
                                <Button color="primary" size="sm" onClick={() => getFaults()}>{t('faults.applyFilters')}</Button>{' '}
                                <Button color="link" size="sm" onClick={() => {
                                    const conf = authService.getConfig();
                                    const resetWh = conf ? conf.whCode : '';
                                    setFilters({ machineId: '', wh: resetWh, shelf: '', reason: '', isSyncedWithERP: '', isResolved: 'false', isOperable: '', timeFrom: '', timeTo: '' });
                                    getFaults(resetWh);
                                }}>{t('faults.resetFilters')}</Button>
                            </div>
                        </CardBody>
                    </Card>
                )}

                <Table responsive hover>
                    <thead>
                        <tr>
                            <th style={{ width: '40px' }}></th>
                            <th>{t('faults.machine')}</th>
                            <th>{t('faults.date')}</th>
                            <th>{t('faults.reason')}</th>
                            <th>{t('faults.operability') || 'Κατάσταση'}</th>
                            <th>{t('faults.synced')}</th>
                            <th>{t('faults.resolved') || 'Resolved'}</th>
                            <th>{t('faults.actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {faults.map(f => (
                            <React.Fragment key={f.id}>
                                <tr onClick={() => toggleRow(f.id)} style={{ cursor: 'pointer' }}>
                                    <td>
                                        <FontAwesomeIcon icon={expandedRows.includes(f.id) ? faChevronUp : faChevronDown} />
                                    </td>
                                    <td>{f.machineId}{f.machine?.title ? ` - ${f.machine.title}` : ''}</td>
                                    <td>{DateTime.fromISO(f.createdAt).setZone('Europe/Athens').toFormat('dd/MM/yyyy HH:mm')}</td>
                                    <td>{f.reason}</td>
                                    <td>
                                        {f.isOperable !== false ? 
                                            <Badge color="info">{t('faults.operableShort') || 'Λειτουργικό'}</Badge> : 
                                            <Badge color="danger">{t('faults.disabledShort') || 'Εκτός λειτουργίας'}</Badge>
                                        }
                                    </td>
                                    <td>
                                        {f.isSyncedWithERP ? 
                                            <Badge color="success">{t('faults.yes')}</Badge> : 
                                            <Badge color="danger">{t('faults.no')}</Badge>
                                        }
                                    </td>
                                    <td>
                                        {f.isResolved ? 
                                            <Badge color="success">{t('faults.yes')}</Badge> : 
                                            <Badge color="danger">{t('faults.no')}</Badge>
                                        }
                                    </td>
                                    <td>
                                        {!f.isSyncedWithERP && (
                                            <Button color="primary" size="sm" className="me-2" onClick={(e) => { e.stopPropagation(); toggleSync(f.id); }}>
                                                <FontAwesomeIcon icon={faSync} /> {t('faults.toggleSync')}
                                            </Button>
                                        )}
                                        {!f.isResolved && (
                                            <Button color="success" size="sm" onClick={(e) => { e.stopPropagation(); resolveFault(f.id); }}>
                                                {t('faults.resolve') || 'Resolve'}
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan="8" style={{ padding: 0, border: 0 }}>
                                        <Collapse isOpen={expandedRows.includes(f.id)}>
                                            <div style={{ padding: '15px', backgroundColor: '#f8f9fa' }}>
                                                <Row>
                                                    <Col md="4">
                                                        <strong>{t('faults.user')}:</strong> {f.user?.name || f.user?.username || f.userId}
                                                    </Col>
                                                    <Col md="4">
                                                        <strong>{t('faults.shelf')}:</strong> {f.shelf}
                                                    </Col>
                                                    {f.isResolved && f.resolvedAt && (
                                                        <Col md="4">
                                                            <strong>{t('faults.resolvedAt') || 'Resolved At'}:</strong> {DateTime.fromISO(f.resolvedAt).setZone('Europe/Athens').toFormat('dd/MM/yyyy HH:mm')}
                                                        </Col>
                                                    )}
                                                </Row>
                                                <Row className="mt-2">
                                                    <Col md="12">
                                                        <strong>{t('faults.message')}:</strong>
                                                        <p className="mb-0">{f.message}</p>
                                                    </Col>
                                                </Row>
                                            </div>
                                        </Collapse>
                                    </td>
                                </tr>
                            </React.Fragment>
                        ))}
                        {faults.length === 0 && !loading && (
                            <tr>
                                <td colSpan="8" className="text-center">{t('faults.noFaults')}</td>
                            </tr>
                        )}
                    </tbody>
                </Table>

                <Modal isOpen={showCreateModal} toggle={() => setShowCreateModal(!showCreateModal)}>
                    <ModalHeader toggle={() => setShowCreateModal(!showCreateModal)}>
                        {t('faults.reportFault') || 'Καταγραφή Βλάβης'}
                    </ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <Label>{t('faults.machine')} *</Label>
                            <Select
                                options={machines}
                                value={machines.find(m => m.value === createForm.machineId)}
                                onChange={(opt) => setCreateForm({ ...createForm, machineId: opt ? opt.value : '' })}
                                placeholder={t('faults.machinePlaceholder')}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>{t('faults.reason')} *</Label>
                            <Input
                                type="select"
                                value={createForm.reason}
                                onChange={(e) => setCreateForm({ ...createForm, reason: e.target.value })}
                            >
                                <option value="">{t('faults.allReasons')}</option>
                                {reasons.map(r => <option key={r} value={r}>{r}</option>)}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label>{t('faults.operability')} *</Label>
                            <Input
                                type="select"
                                value={createForm.isOperable.toString()}
                                onChange={(e) => setCreateForm({ ...createForm, isOperable: e.target.value === 'true' })}
                            >
                                <option value="true">{t('faults.operable') || 'Λειτουργικό'}</option>
                                <option value="false">{t('faults.disabled') || 'Εκτός λειτουργίας (Ακινητοποιημένο)'}</option>
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label>{t('faults.shelf')}</Label>
                            <Select
                                isClearable
                                options={areas}
                                value={areas.find(a => a.value === createForm.shelf)}
                                onChange={(opt) => setCreateForm({ ...createForm, shelf: opt ? opt.value : '' })}
                                placeholder={t('faults.shelfPlaceholder')}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>{t('faults.message')}</Label>
                            <Input
                                type="textarea"
                                rows="3"
                                value={createForm.message}
                                onChange={(e) => setCreateForm({ ...createForm, message: e.target.value })}
                                placeholder="Περιγραφή βλάβης..."
                            />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setShowCreateModal(false)}>
                            {t('schedules.cancel') || 'Cancel'}
                        </Button>
                        <Button color="danger" onClick={handleCreateFault} disabled={createLoading || !createForm.machineId || !createForm.reason}>
                            {createLoading ? '...' : (t('faults.createFault') || 'Submit')}
                        </Button>
                    </ModalFooter>
                </Modal>
            </CardBody>
        </Card>
    );
};

export default withTranslation(Faults);
